// @ts-nocheck -- staged migration: add explicit types before removing this directive.
import React, { useDeferredValue, useId, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, useParams } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faXmark } from "@fortawesome/free-solid-svg-icons";
import Header from "../header/HeaderComponent";
import Footer from "../footer/FooterComponent";
import { blogPosts, getBlogPostBySlug } from "../../consts/blogPosts";
import { kanjiTextbookOrder } from "../../consts/kanjiTextbookOrder";
import { estimateReadingMinutes } from "../../utils/blogReadingTime";
import { buildKanjiSearchIndex, searchKanjiIndex } from "../../utils/kanjiSearch";
import Seo from "../Seo/SeoComponent";
import TableCell from "../ui/TableCell/TableCell";
import BackLink from "../ui/BackLink/BackLink";
import BlogSubscribePrompt from "../NewsletterForm/BlogSubscribePrompt";
import styles from "./blogPostPage.module.scss";

function formatDate(dateString, locale = "en-US") {
    return new Date(`${dateString}T00:00:00`).toLocaleDateString(locale, {
        year: "numeric",
        month: "long",
        day: "numeric",
    });
}

const jlptLevels = [5, 4, 3, 2, 1];

// Surfaces kanji covered by a common teaching order (see kanjiTextbookOrder)
// first, in that order, ahead of everything else in the jōyō list.
const textbookPriority = new Map(kanjiTextbookOrder.map((symbol, index) => [symbol, index]));

const identity = (path) => path;
const englishMeaning = (symbol, meaning) => meaning;

function KanjiMeaningGrid({ items, localize = identity, meaningFor = englishMeaning }) {
    const { t } = useTranslation("blogPage");
    const [activeLevel, setActiveLevel] = useState(5);
    const [query, setQuery] = useState("");
    const inputRef = useRef(null);
    const inputId = useId();
    // Typing stays instant; the (potentially 3,000-tile) grid catches up at low priority.
    const deferredQuery = useDeferredValue(query).trim();
    const isSearching = deferredQuery !== "";

    // Textbook order once per list; level and search filters below preserve it.
    const sortedItems = useMemo(
        () =>
            [...items].sort((a, b) => {
                const priorityA = textbookPriority.has(a.symbol) ? textbookPriority.get(a.symbol) : Infinity;
                const priorityB = textbookPriority.has(b.symbol) ? textbookPriority.get(b.symbol) : Infinity;
                return priorityA - priorityB;
            }),
        [items]
    );
    const searchIndex = useMemo(() => buildKanjiSearchIndex(sortedItems, meaningFor), [sortedItems, meaningFor]);
    const levelCounts = useMemo(
        () => jlptLevels.map((level) => items.filter((item) => item.jlptLevel === level).length),
        [items]
    );
    const filtered = useMemo(() => {
        // While searching, look across every level so a match is never hidden by the N-level filter.
        if (isSearching) return searchKanjiIndex(searchIndex, deferredQuery);
        return activeLevel === "all" ? sortedItems : sortedItems.filter((item) => item.jlptLevel === activeLevel);
    }, [isSearching, deferredQuery, searchIndex, sortedItems, activeLevel]);

    const clearSearch = () => {
        setQuery("");
        inputRef.current?.focus();
    };

    const handleKeyDown = (event) => {
        // Escape also cancels an IME composition; leave that to the IME.
        if (event.key === "Escape" && !event.nativeEvent.isComposing && query !== "") {
            event.preventDefault();
            setQuery("");
        }
    };

    let statusText = "";
    if (isSearching) {
        statusText = filtered.length === 0 ? t("post.search.noResults") : t("post.search.found", { count: filtered.length });
    }

    return (
        <div>
            <div className={styles.kanjiSearch}>
                <label htmlFor={inputId} className={styles.visuallyHidden}>
                    {t("post.search.label")}
                </label>
                <input
                    id={inputId}
                    ref={inputRef}
                    type="search"
                    className={styles.kanjiSearchInput}
                    value={query}
                    onChange={(event) => setQuery(event.target.value)}
                    onKeyDown={handleKeyDown}
                    placeholder={t("post.search.placeholder")}
                    inputMode="search"
                    autoComplete="off"
                    autoCorrect="off"
                    autoCapitalize="off"
                    spellCheck={false}
                />
                {query !== "" && (
                    <button
                        type="button"
                        className={styles.kanjiSearchClear}
                        onClick={clearSearch}
                        aria-label={t("post.search.clear")}
                    >
                        <FontAwesomeIcon icon={faXmark} />
                    </button>
                )}
            </div>
            <div className={styles.jlptFilterRow}>
                <button
                    type="button"
                    className={`${styles.jlptFilterButton} ${!isSearching && activeLevel === "all" ? styles.jlptFilterButtonActive : ""}`}
                    onClick={() => setActiveLevel("all")}
                    disabled={isSearching}
                >
                    {t("post.all", { count: items.length })}
                </button>
                {jlptLevels.map((level, levelIndex) => (
                    <button
                        key={level}
                        type="button"
                        className={`${styles.jlptFilterButton} ${!isSearching && activeLevel === level ? styles.jlptFilterButtonActive : ""}`}
                        onClick={() => setActiveLevel(level)}
                        disabled={isSearching}
                    >
                        N{level} ({levelCounts[levelIndex]})
                    </button>
                ))}
            </div>
            <p className={styles.kanjiSearchStatus} role="status" aria-live="polite">
                {isSearching && (
                    <>
                        <span>{statusText}</span>
                        <span className={styles.kanjiSearchNote}> · {t("post.search.allLevels")}</span>
                    </>
                )}
            </p>
            {isSearching && filtered.length === 0 ? (
                <p className={styles.kanjiSearchEmpty}>{t("post.search.empty", { query: deferredQuery })}</p>
            ) : (
                <div className={styles.kanjiMeaningGrid}>
                    {filtered.map((item) => (
                        <Link
                            key={item.symbol}
                            to={localize(`/kanji/${encodeURIComponent(item.symbol)}`)}
                            className={styles.kanjiMeaningTile}
                        >
                            <span className={styles.kanjiMeaningGlyph}>{item.symbol}</span>
                            <span className={styles.kanjiMeaningText}>{meaningFor(item.symbol, item.meaning)}</span>
                        </Link>
                    ))}
                </div>
            )}
        </div>
    );
}

function ContentBlock({ block, index, localize = identity, meaningFor = englishMeaning }) {
    if (block.callout) {
        return (
            <aside key={index} className={styles.callout} role="note">
                {block.callout}
            </aside>
        );
    }

    if (block.heading) {
        return (
            <h2 key={index} className={styles.sectionHeading}>
                {block.heading}
            </h2>
        );
    }

    if (block.kanjiMeaningGrid) {
        return <KanjiMeaningGrid key={index} items={block.kanjiMeaningGrid} localize={localize} meaningFor={meaningFor} />;
    }

    if (block.table) {
        const { caption, headers, rows, rowHeaders = true } = block.table;
        return (
            <figure key={index} className={styles.tableFigure}>
                <div className={styles.tableScroll} tabIndex={0} role="region" aria-label={caption}>
                    <table className={styles.table}>
                        <caption className={styles.tableCaption}>{caption}</caption>
                        <thead>
                            <tr>
                                {headers.map((header) => (
                                    <th key={header} scope="col">
                                        {header}
                                    </th>
                                ))}
                            </tr>
                        </thead>
                        <tbody>
                            {rows.map((row, rowIndex) => (
                                <tr key={rowIndex}>
                                    {row.map((cell, cellIndex) =>
                                        rowHeaders && cellIndex === 0 ? (
                                            <th key={cellIndex} scope="row">
                                                <TableCell cell={cell} />
                                            </th>
                                        ) : (
                                            <td key={cellIndex}>
                                                <TableCell cell={cell} />
                                            </td>
                                        )
                                    )}
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </figure>
        );
    }

    if (block.list) {
        return (
            <ul key={index} className={styles.list}>
                {block.list.map((item) => (
                    <li key={item}>{item}</li>
                ))}
            </ul>
        );
    }

    if (block.parts) {
        return (
            <p key={index} className={styles.paragraph}>
                {block.parts.map((part, partIndex) => {
                    if (typeof part === "string") {
                        return <React.Fragment key={partIndex}>{part}</React.Fragment>;
                    }
                    if (part.href) {
                        return (
                            <a
                                key={partIndex}
                                href={part.href}
                                target="_blank"
                                rel="noopener noreferrer"
                                className={styles.inlineLink}
                            >
                                {part.label}
                            </a>
                        );
                    }
                    return (
                        <Link key={partIndex} to={localize(part.to)} className={styles.inlineLink}>
                            {part.label}
                        </Link>
                    );
                })}
            </p>
        );
    }

    return (
        <p key={index} className={styles.paragraph}>
            {block.text}
        </p>
    );
}

export function blogPostAlternates(slug) {
    const post = getBlogPostBySlug(slug);
    if (!post) return [];
    const englishTwin = blogPosts.find((candidate) => candidate.ptSlug === slug);
    if (post.lang === "pt-PT") {
        return englishTwin
            ? [
                  { hrefLang: "en", path: `/blog/${englishTwin.slug}` },
                  { hrefLang: "pt-PT", path: `/blog/${slug}` },
              ]
            : [];
    }
    return [
        { hrefLang: "en", path: `/blog/${slug}` },
        { hrefLang: "pt-PT", path: post.ptSlug ? `/blog/${post.ptSlug}` : `/pt/blog/${slug}` },
    ];
}

export function BlogPostNotFound({ path, blogPath, localeLang }) {
    const { t } = useTranslation("blogPage");
    return (
        <div className={styles.page}>
            <Seo title={t("post.notFoundTitle")} path={path} noindex lang={localeLang} />
            <Header />

            <main className={styles.main}>
                <section className={styles.container}>
                    <h1 className={styles.title}>{t("post.notFoundTitle")}</h1>
                    <p className={styles.description}>{t("post.notFoundDescription")}</p>
                    <BackLink to={blogPath} label={t("post.backToBlog")} />
                </section>
            </main>

            <Footer />
        </div>
    );
}

export function BlogPostView({ post, path, blogPath = "/blog", localize = identity, meaningFor = englishMeaning, alternates = [] }) {
    const { t } = useTranslation("blogPage");
    const readingMinutes = estimateReadingMinutes(post.content);
    return (
        <div className={styles.page}>
            <Seo
                title={post.title}
                description={post.excerpt}
                path={path}
                lang={post.lang}
                alternates={alternates}
                keywords={post.keywords}
            />
            <Header />

            <main className={styles.main}>
                <article className={styles.container}>
                    <BackLink to={blogPath} label={t("post.backToBlog")} />

                    <header className={styles.postHeader}>
                        <div className={styles.postMeta}>
                            <time dateTime={post.date}>{formatDate(post.date, post.lang)}</time>
                            <span className={styles.postReadingTime}>
                                {t("post.readingTime", { count: readingMinutes })}
                            </span>
                            {post.tags?.map((tag) => (
                                <Link
                                    key={tag}
                                    to={`${blogPath}?tag=${encodeURIComponent(tag)}`}
                                    className={styles.postTag}
                                >
                                    {t(`tags.${tag}`, { defaultValue: tag })}
                                </Link>
                            ))}
                        </div>
                        <h1 className={styles.title}>{post.title}</h1>
                    </header>

                    <div className={styles.body}>
                        {post.content.map((block, index) => (
                            <ContentBlock key={index} block={block} index={index} localize={localize} meaningFor={meaningFor} />
                        ))}
                    </div>

                    <BlogSubscribePrompt />
                </article>
            </main>

            <Footer />
        </div>
    );
}

function BlogPostPage() {
    const { slug } = useParams();
    const post = getBlogPostBySlug(slug);

    if (!post) {
        return <BlogPostNotFound path={`/blog/${slug}`} blogPath="/blog" />;
    }

    return <BlogPostView post={post} path={`/blog/${post.slug}`} alternates={blogPostAlternates(post.slug)} />;
}

export default BlogPostPage;
